import { useEffect, useState } from "react";
import { api, formatApiError, formatPrice } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, BookOpen, Users, DollarSign, TrendingUp, X, Loader2, Tag } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const CATS = ["Inteligencia Artificial", "Informática", "Ingeniería de Sistemas", "Ingeniería Civil", "Contabilidad", "Administración"];
const LEVELS = ["Básico", "Intermedio", "Avanzado"];

const EMPTY = {
  title: "", category: CATS[0], short_description: "", description: "", price: 149.9,
  currency: "pen", level: "Intermedio", duration_hours: 10, instructor_name: "",
  instructor_title: "", instructor_picture: "", image_url: "", is_published: true,
  rating: 4.8, students_count: 0, syllabus: [], lessons: [],
};

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [courses, setCourses] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [sales, setSales] = useState(null);
  const [couponForm, setCouponForm] = useState({ code: "", discount_percent: 20, max_uses: 0 });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/courses").then(({ data }) => setCourses(Array.isArray(data) ? data : [])).catch(() => {});
    api.get("/admin/coupons").then(({ data }) => setCoupons(Array.isArray(data) ? data : [])).catch(() => {});
    api.get("/admin/sales").then(({ data }) => setSales(data)).catch(() => {});
  };
  useEffect(load, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...EMPTY, ...c }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price), duration_hours: parseFloat(form.duration_hours), students_count: parseInt(form.students_count) || 0, rating: parseFloat(form.rating) || 4.8 };
      if (editing) await api.put(`/admin/courses/${editing.id}`, payload);
      else await api.post("/admin/courses", payload);
      toast.success(editing ? "Curso actualizado" : "Curso creado");
      setOpen(false); load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const del = async (c) => {
    if (!window.confirm(`¿Eliminar "${c.title}"?`)) return;
    try { await api.delete(`/admin/courses/${c.id}`); toast.success("Curso eliminado"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const createCoupon = async () => {
    if (!couponForm.code.trim()) { toast.error("Ingresa un código"); return; }
    try {
      await api.post("/admin/coupons", { code: couponForm.code, discount_percent: parseFloat(couponForm.discount_percent), active: true, max_uses: parseInt(couponForm.max_uses) || 0 });
      toast.success("Cupón creado");
      setCouponForm({ code: "", discount_percent: 20, max_uses: 0 });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const delCoupon = async (c) => {
    try { await api.delete(`/admin/coupons/${c.id}`); toast.success("Cupón eliminado"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // syllabus/lessons helpers
  const addModule = () => setForm({ ...form, syllabus: [...form.syllabus, { title: "", items: [] }] });
  const updModule = (i, k, v) => { const s = [...form.syllabus]; s[i] = { ...s[i], [k]: k === "items" ? v.split(",").map((x) => x.trim()).filter(Boolean) : v }; setForm({ ...form, syllabus: s }); };
  const delModule = (i) => setForm({ ...form, syllabus: form.syllabus.filter((_, x) => x !== i) });

  const addLesson = () => setForm({ ...form, lessons: [...form.lessons, { title: "", duration: "", video_url: "", description: "", is_preview: false }] });
  const updLesson = (i, k, v) => { const l = [...form.lessons]; l[i] = { ...l[i], [k]: v }; setForm({ ...form, lessons: l }); };
  const delLesson = (i) => setForm({ ...form, lessons: form.lessons.filter((_, x) => x !== i) });

  const STATS = stats ? [
    { icon: BookOpen, label: "Cursos", value: stats.total_courses, color: "#00E5FF" },
    { icon: Users, label: "Estudiantes", value: stats.total_students, color: "#7000FF" },
    { icon: TrendingUp, label: "Inscripciones", value: stats.total_enrollments, color: "#00E6BA" },
    { icon: DollarSign, label: "Ingresos", value: formatPrice(stats.revenue), color: "#FFB020" },
  ] : [];

  const fieldCls = "bg-[#13141C] border-white/10 rounded-md focus:border-[#00E5FF] focus-visible:ring-1 focus-visible:ring-[#00E5FF] text-white";

  return (
    <div className="App">
      <Navbar />
      <section className="pt-28 pb-16 max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between mb-10">
          <div>
            <span className="font-mono text-xs uppercase tracking-widest text-[#00E5FF]">// Administración</span>
            <h1 className="font-heading font-bold text-4xl tracking-tight mt-2">Panel de control</h1>
          </div>
          <Button data-testid="admin-new-course-btn" onClick={openNew} className="bg-[#00E5FF] text-black font-semibold hover:bg-white rounded-md h-11">
            <Plus className="w-4 h-4 mr-2" /> Nuevo curso
          </Button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {STATS.map((s) => (
            <div key={s.label} className="bg-[#0C0D12] border border-white/10 rounded-md p-6">
              <s.icon className="w-6 h-6 mb-4" style={{ color: s.color }} />
              <p className="font-mono text-3xl font-bold">{s.value}</p>
              <p className="font-body text-sm text-[#8A8D98] mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        {/* PANEL DE VENTAS */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <TrendingUp className="w-5 h-5 text-[#00E5FF]" />
            <h2 className="font-heading font-bold text-2xl tracking-tight">Panel de ventas</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 bg-[#0C0D12] border border-white/10 rounded-md p-6" data-testid="sales-chart">
              <p className="font-mono text-xs uppercase tracking-widest text-[#525560] mb-6">Ingresos por mes (S/)</p>
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <BarChart data={sales?.monthly || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                    <XAxis dataKey="month" stroke="#525560" tick={{ fontSize: 12, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                    <YAxis stroke="#525560" tick={{ fontSize: 12, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                    <Tooltip cursor={{ fill: "rgba(0,229,255,0.05)" }}
                      contentStyle={{ background: "#13141C", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontFamily: "IBM Plex Sans" }}
                      labelStyle={{ color: "#fff" }} formatter={(v) => [`S/ ${v}`, "Ingresos"]} />
                    <Bar dataKey="revenue" fill="#00E5FF" radius={[4, 4, 0, 0]} maxBarSize={48} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="bg-[#0C0D12] border border-white/10 rounded-md p-6">
              <p className="font-mono text-xs uppercase tracking-widest text-[#525560] mb-6">Cursos más vendidos</p>
              <div className="space-y-4">
                {sales?.top_courses?.length ? sales.top_courses.map((t, i) => (
                  <div key={i} data-testid={`top-course-${i}`} className="flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="font-mono text-sm text-[#00E5FF] w-5 shrink-0">{i + 1}</span>
                      <span className="font-body text-sm truncate">{t.title}</span>
                    </div>
                    <div className="text-right shrink-0 ml-3">
                      <p className="font-mono text-sm text-white">{formatPrice(t.revenue)}</p>
                      <p className="font-mono text-xs text-[#525560]">{t.sales} ventas</p>
                    </div>
                  </div>
                )) : <p className="font-body text-sm text-[#525560]">Aún no hay ventas registradas.</p>}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-[#0C0D12] border border-white/10 rounded-md overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10 font-mono text-xs uppercase tracking-wider text-[#525560]">
                <th className="text-left p-4">Curso</th>
                <th className="text-left p-4 hidden md:table-cell">Categoría</th>
                <th className="text-left p-4 hidden md:table-cell">Precio</th>
                <th className="text-left p-4 hidden lg:table-cell">Estado</th>
                <th className="text-right p-4">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {courses.map((c) => (
                <tr key={c.id} data-testid={`admin-row-${c.slug}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <img src={c.image_url} alt="" className="w-12 h-8 object-cover rounded" />
                      <span className="font-body text-sm">{c.title}</span>
                    </div>
                  </td>
                  <td className="p-4 hidden md:table-cell font-mono text-xs text-[#8A8D98]">{c.category}</td>
                  <td className="p-4 hidden md:table-cell font-mono text-sm text-[#00E5FF]">{formatPrice(c.price, c.currency)}</td>
                  <td className="p-4 hidden lg:table-cell">
                    <span className={`font-mono text-[10px] uppercase px-2 py-1 rounded ${c.is_published ? "bg-[#00E6BA]/10 text-[#00E6BA]" : "bg-white/5 text-[#525560]"}`}>{c.is_published ? "Publicado" : "Borrador"}</span>
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex justify-end gap-2">
                      <Button data-testid={`admin-edit-${c.slug}`} onClick={() => openEdit(c)} variant="ghost" size="icon" className="text-[#8A8D98] hover:text-[#00E5FF] hover:bg-white/5"><Pencil className="w-4 h-4" /></Button>
                      <Button data-testid={`admin-delete-${c.slug}`} onClick={() => del(c)} variant="ghost" size="icon" className="text-[#8A8D98] hover:text-destructive hover:bg-white/5"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* CUPONES */}
        <div className="mt-14">
          <div className="flex items-center gap-3 mb-6">
            <Tag className="w-5 h-5 text-[#00E6BA]" />
            <h2 className="font-heading font-bold text-2xl tracking-tight">Cupones de descuento</h2>
          </div>
          <div className="bg-[#0C0D12] border border-white/10 rounded-md p-6 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              <div><Label className="text-sm text-[#8A8D98] mb-1.5 block">Código</Label>
                <Input data-testid="coupon-code-input" value={couponForm.code} onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })} placeholder="PROMO20" className={fieldCls} /></div>
              <div><Label className="text-sm text-[#8A8D98] mb-1.5 block">Descuento (%)</Label>
                <Input data-testid="coupon-discount-input" type="number" value={couponForm.discount_percent} onChange={(e) => setCouponForm({ ...couponForm, discount_percent: e.target.value })} className={fieldCls} /></div>
              <div><Label className="text-sm text-[#8A8D98] mb-1.5 block">Usos máx. (0=∞)</Label>
                <Input type="number" value={couponForm.max_uses} onChange={(e) => setCouponForm({ ...couponForm, max_uses: e.target.value })} className={fieldCls} /></div>
              <Button data-testid="create-coupon-btn" onClick={createCoupon} className="bg-[#00E6BA] text-black font-semibold hover:bg-white rounded-md h-11"><Plus className="w-4 h-4 mr-2" /> Crear cupón</Button>
            </div>
          </div>
          <div className="bg-[#0C0D12] border border-white/10 rounded-md overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10 font-mono text-xs uppercase tracking-wider text-[#525560]">
                  <th className="text-left p-4">Código</th>
                  <th className="text-left p-4">Descuento</th>
                  <th className="text-left p-4 hidden md:table-cell">Usos</th>
                  <th className="text-right p-4">Acción</th>
                </tr>
              </thead>
              <tbody>
                {coupons.length === 0 ? (
                  <tr><td colSpan={4} className="p-6 text-center font-body text-sm text-[#525560]">Aún no hay cupones.</td></tr>
                ) : coupons.map((c) => (
                  <tr key={c.id} data-testid={`coupon-row-${c.code}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="p-4 font-mono text-sm text-[#00E6BA]">{c.code}</td>
                    <td className="p-4 font-mono text-sm">{c.discount_percent}%</td>
                    <td className="p-4 hidden md:table-cell font-mono text-sm text-[#8A8D98]">{c.uses}{c.max_uses ? `/${c.max_uses}` : ""}</td>
                    <td className="p-4 text-right">
                      <Button data-testid={`coupon-delete-${c.code}`} onClick={() => delCoupon(c)} variant="ghost" size="icon" className="text-[#8A8D98] hover:text-destructive hover:bg-white/5"><Trash2 className="w-4 h-4" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0C0D12] border border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Editar curso" : "Nuevo curso"}</DialogTitle>
            <DialogDescription className="font-body text-sm text-[#8A8D98]">Completa los datos, el temario y las lecciones del curso.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-sm text-[#8A8D98] mb-1.5 block">Título</Label>
              <Input data-testid="form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={fieldCls} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-sm text-[#8A8D98] mb-1.5 block">Categoría</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="form-category" className={fieldCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#13141C] border-white/10 text-white">{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-sm text-[#8A8D98] mb-1.5 block">Nivel</Label>
                <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
                  <SelectTrigger data-testid="form-level" className={fieldCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#13141C] border-white/10 text-white">{LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div><Label className="text-sm text-[#8A8D98] mb-1.5 block">Descripción corta</Label>
              <Input data-testid="form-short" value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} className={fieldCls} /></div>
            <div><Label className="text-sm text-[#8A8D98] mb-1.5 block">Descripción completa</Label>
              <Textarea data-testid="form-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={fieldCls} rows={3} /></div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label className="text-sm text-[#8A8D98] mb-1.5 block">Precio (S/)</Label>
                <Input data-testid="form-price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={fieldCls} /></div>
              <div><Label className="text-sm text-[#8A8D98] mb-1.5 block">Horas</Label>
                <Input type="number" value={form.duration_hours} onChange={(e) => setForm({ ...form, duration_hours: e.target.value })} className={fieldCls} /></div>
              <div><Label className="text-sm text-[#8A8D98] mb-1.5 block">Alumnos</Label>
                <Input type="number" value={form.students_count} onChange={(e) => setForm({ ...form, students_count: e.target.value })} className={fieldCls} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-sm text-[#8A8D98] mb-1.5 block">Instructor</Label>
                <Input value={form.instructor_name} onChange={(e) => setForm({ ...form, instructor_name: e.target.value })} className={fieldCls} /></div>
              <div><Label className="text-sm text-[#8A8D98] mb-1.5 block">Cargo instructor</Label>
                <Input value={form.instructor_title} onChange={(e) => setForm({ ...form, instructor_title: e.target.value })} className={fieldCls} /></div>
            </div>
            <div><Label className="text-sm text-[#8A8D98] mb-1.5 block">URL imagen del curso</Label>
              <Input data-testid="form-image" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className={fieldCls} /></div>
            <div><Label className="text-sm text-[#8A8D98] mb-1.5 block">URL foto del instructor</Label>
              <Input value={form.instructor_picture} onChange={(e) => setForm({ ...form, instructor_picture: e.target.value })} className={fieldCls} /></div>

            {/* Syllabus */}
            <div className="border-t border-white/10 pt-4">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-sm text-white">Temario (módulos)</Label>
                <Button type="button" onClick={addModule} variant="outline" size="sm" className="border-white/15 bg-transparent text-[#00E5FF] rounded-md h-8"><Plus className="w-3 h-3 mr-1" /> Módulo</Button>
              </div>
              {form.syllabus.map((m, i) => (
                <div key={i} className="bg-[#13141C] rounded-md p-3 mb-2 space-y-2">
                  <div className="flex gap-2">
                    <Input placeholder="Título del módulo" value={m.title} onChange={(e) => updModule(i, "title", e.target.value)} className={fieldCls} />
                    <Button type="button" onClick={() => delModule(i)} variant="ghost" size="icon" className="text-[#525560] hover:text-destructive"><X className="w-4 h-4" /></Button>
                  </div>
                  <Input placeholder="Temas separados por coma" value={(m.items || []).join(", ")} onChange={(e) => updModule(i, "items", e.target.value)} className={fieldCls} />
                </div>
              ))}
            </div>

            {/* Lessons */}
            <div className="border-t border-white/10 pt-4">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-sm text-white">Lecciones</Label>
                <Button type="button" onClick={addLesson} variant="outline" size="sm" className="border-white/15 bg-transparent text-[#00E5FF] rounded-md h-8"><Plus className="w-3 h-3 mr-1" /> Lección</Button>
              </div>
              {form.lessons.map((l, i) => (
                <div key={i} className="bg-[#13141C] rounded-md p-3 mb-2 space-y-2">
                  <div className="flex gap-2">
                    <Input placeholder="Título" value={l.title} onChange={(e) => updLesson(i, "title", e.target.value)} className={fieldCls} />
                    <Input placeholder="Duración" value={l.duration} onChange={(e) => updLesson(i, "duration", e.target.value)} className={`${fieldCls} w-28`} />
                    <Button type="button" onClick={() => delLesson(i)} variant="ghost" size="icon" className="text-[#525560] hover:text-destructive"><X className="w-4 h-4" /></Button>
                  </div>
                  <Input placeholder="URL del video (mp4)" value={l.video_url} onChange={(e) => updLesson(i, "video_url", e.target.value)} className={fieldCls} />
                  <div className="flex items-center gap-2">
                    <Switch checked={l.is_preview} onCheckedChange={(v) => updLesson(i, "is_preview", v)} />
                    <span className="font-body text-xs text-[#8A8D98]">Vista previa gratis</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2 border-t border-white/10 pt-4">
              <Switch data-testid="form-published" checked={form.is_published} onCheckedChange={(v) => setForm({ ...form, is_published: v })} />
              <span className="font-body text-sm text-[#8A8D98]">Publicado</span>
            </div>

            <Button data-testid="form-save" onClick={save} disabled={saving} className="w-full bg-[#00E5FF] text-black font-semibold hover:bg-white rounded-md h-11">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Guardar cambios" : "Crear curso"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
